import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { LogOut, Menu, X, Moon, Sun, Inbox, BarChart3, Database, TrendingUp, Users, Settings, Lightbulb, Send, FileText, PieChart, Sparkles, Workflow, Layers, BookOpen, Image as ImageIcon, LayoutGrid, Calendar as CalendarIcon, History } from 'lucide-react';
import ThemeToggle from './ThemeToggle';
import NotificationBell from './NotificationBell';

const Sidebar = ({ currentPage, setCurrentPage }) => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [brandName, setBrandName] = useState('');

  const isAdmin = user?.role === 'admin';
  const isViewer = user?.role === 'viewer';
  
  // Fetch brand name for brand users
  React.useEffect(() => {
    const fetchBrandName = async () => {
      if (user?.brand_id && user?.role === 'brand') {
        try {
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/brands`);
          const brands = await response.json();
          const userBrand = brands.find(b => b.id === user.brand_id);
          if (userBrand) {
            setBrandName(userBrand.name);
          }
        } catch (error) {
          console.error('Failed to fetch brand name:', error);
        }
      }
    };
    fetchBrandName();
  }, [user]);

  const navItems = isAdmin ? [
    {
      category: 'Campaigns',
      items: [
        { id: 'queue', label: 'Request Queue', icon: <Inbox size={18} /> },
        { id: 'booking-calendar', label: 'Booking Calendar', icon: <CalendarIcon size={18} /> },
        { id: 'campaigns-data', label: 'Campaign Analytics', icon: <Database size={18} /> },
      ]
    },
    {
      category: 'Analytics',
      items: [
        { id: 'one-view-analyzer', label: 'One View Analyzer', icon: <LayoutGrid size={18} /> },
        { id: 'analytics', label: 'Trends', icon: <TrendingUp size={18} /> },
        { id: 'automation', label: 'Automation Analytics', icon: <Sparkles size={18} /> },
        { id: 'flow-builder', label: 'Flow Builder', icon: <Workflow size={18} /> },
      ]
    },
    {
      category: 'Settings',
      items: [
        { id: 'users', label: 'User Management', icon: <Users size={18} /> },
        { id: 'admin-panel', label: 'Admin', icon: <Layers size={18} /> },
        { id: 'backoffice', label: 'Control Panel', icon: <Settings size={18} /> },
        { id: 'audit-log', label: 'Audit Log', icon: <History size={18} /> },
        { id: 'feature-requests', label: 'Feature Requests', icon: <Lightbulb size={18} /> },
      ]
    }
  ] : [
    {
      category: 'Campaigns',
      items: [
        { id: 'request', label: 'Request Campaign', icon: <Send size={18} /> },
        { id: 'my-requests', label: 'My Requests', icon: <FileText size={18} /> },
      ]
    },
    {
      category: 'Analytics',
      items: [
        { id: 'campaigns-data', label: 'Campaign Analytics', icon: <Database size={18} /> },
        { id: 'one-view-analyzer', label: 'One View Analyzer', icon: <LayoutGrid size={18} /> },
        { id: 'analytics', label: 'Trends', icon: <TrendingUp size={18} /> },
        { id: 'automation', label: 'Automation Analytics', icon: <Sparkles size={18} /> },
        { id: 'flow-builder', label: 'Flow Builder', icon: <Workflow size={18} /> },
      ]
    },
    {
      category: 'Library',
      items: [
        { id: 'content-library', label: 'Content Library', icon: <BookOpen size={18} /> },
        { id: 'creative-library', label: 'Creative Library', icon: <ImageIcon size={18} /> },
      ]
    }
  ];

  return (
    <>
      {/* Mobile menu button */}
      <button
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        className="md:hidden fixed top-4 left-4 z-50 p-2 rounded-lg bg-slate-800 text-white"
      >
        {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Sidebar */}
      <div
        className={`
          fixed md:static inset-y-0 left-0 z-40 w-64 
          bg-white border-r border-slate-200
          transform transition-transform duration-300 ease-in-out
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
        `}
      >
        <div className="flex flex-col h-full">
          {/* Logo - Square with proper hierarchy */}
          <div className="p-5 pb-4 border-b border-slate-100">
            <div className="flex items-center gap-3">
              {/* Square logo like Favicon - brand color with white text */}
              <div className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-2xl" 
                   style={{ backgroundColor: '#2EB4AC' }}>
                S
              </div>
              {/* Brand text stacked - STREAM by wellversed */}
              <div className="flex-1">
                <h1 className="text-2xl font-bold leading-none" style={{ color: '#2EB4AC' }}>
                  STREAM
                </h1>
                <p className="text-base font-medium mt-1 text-slate-600">
                  by wellversed<sup className="text-[10px]">™</sup>
                </p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-6 overflow-y-auto">
            {navItems.map((section, idx) => (
              <div key={idx}>
                <div className="px-3 mb-2">
                  <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    {section.category}
                  </h3>
                </div>
                <div className="space-y-0.5">
                  {section.items.map((item) => {
                    const isActive = currentPage === item.id;
                    return (
                      <button
                        key={item.id}
                        onClick={() => {
                          setCurrentPage(item.id);
                          setMobileMenuOpen(false);
                        }}
                        className={`
                          w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left text-sm font-medium
                          transition-all duration-200
                          ${
                            isActive
                              ? 'bg-teal-50 text-teal-700'
                              : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                          }
                        `}
                      >
                        <span className="flex-shrink-0">
                          {item.icon}
                        </span>
                        <span>{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>

          {/* Logout */}
          <div className="px-3 py-3 border-t border-slate-100 space-y-0.5">
            {!isAdmin && (
              <button
                onClick={() => setCurrentPage('request-feature')}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors text-sm font-medium"
              >
                <Lightbulb size={18} />
                <span>Request Feature</span>
              </button>
            )}
            
            <button
              onClick={logout}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors text-sm font-medium"
            >
              <LogOut size={18} />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </div>

      {/* Overlay for mobile */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
    </>
  );
};

export default Sidebar;
